// Standalone Nav + Footer for v12 conversational variant.
// Mountable on interior pages via veaury — uses shared Link, no Inertia react import.
//
// Note: V12ConversationalNav is the bare nav row. On interior pages, mount it
// inside a container with `background: #0f1419` (theme.bg) + the radial-gradient
// overlay if you want to mirror the homepage hero treatment, otherwise the bare
// row sits directly on the page background.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#0f1419', fg: '#f4f3ee', muted: '#9aa0aa',
  border: 'rgba(255,255,255,0.1)', accent: '#7cf5b9', accentFg: '#0d1117',
  font: '"Inter", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, monospace',
  glassBorder: 'rgba(124,245,185,0.18)',
  creamMuted: 'rgba(244,243,238,0.65)',
  altBg: '#0a0e13',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navLinks = [
  { label: 'Solar', href: '/photovoltaik' },
  { label: 'Wärmepumpe', href: '/wärmepumpe' },
  { label: 'Marktanalyse', href: '/marktanalyse' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Magazin', href: '/ratgeber' },
];

const footerCols = [
  { h: 'LÖSUNGEN', items: [
    { label: 'Solar', href: '/photovoltaik' },
    { label: 'Wärmepumpe', href: '/wärmepumpe' },
    { label: 'Produkte', href: '/produkte' },
  ] },
  { h: 'WISSEN', items: [
    { label: 'Magazin', href: '/ratgeber' },
    { label: 'Förderung', href: '/förderung-finder' },
    { label: 'Glossar', href: '/glossar' },
  ] },
  { h: 'UNTERNEHMEN', items: [
    { label: 'Über uns', href: '/über-uns' },
    { label: 'Partner', href: '/partner' },
    { label: 'Karriere', href: '/karriere' },
  ] },
  { h: 'RECHTLICHES', items: [
    { label: 'Impressum', href: '/impressum' },
    { label: 'Datenschutz', href: '/datenschutz' },
    { label: 'AGB', href: '/agb' },
    { label: 'Widerruf', href: '/widerruf' },
  ] },
];

export function V12ConversationalNav({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <div style={{
      position: 'relative',
      padding: isMobile ? '14px 16px' : '20px 36px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: isMobile ? 15 : 17, color: theme.fg, textDecoration: 'none' }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: theme.accent }}/>
        {brand}
        {!isMobile && (
          <span style={{ fontSize: 11, fontWeight: 600, color: theme.accent, padding: '2px 8px', borderRadius: 999, background: 'rgba(124, 245, 185, 0.13)', marginLeft: 4 }}>AI-KOMBI</span>
        )}
      </Link>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 22, fontSize: 13, color: theme.muted }}>
            {navLinks.map((l) => <Link key={l.label} href={l.href} style={{ color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>)}
          </div>
          <Link href="/angebot-vergleichen" style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600, background: theme.accent, color: theme.accentFg, border: 'none', borderRadius: 999, textDecoration: 'none', display: 'inline-block' }}>
            ✦ Beratung starten
          </Link>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 15 }}>{l.label}</Link>
          ))}
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 14px', fontSize: 14, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 999,
            textAlign: 'center', textDecoration: 'none', display: 'block', marginTop: 4,
          }}>
            ✦ Beratung starten
          </Link>
        </div>
      )}
    </div>
  );
}

export function V12ConversationalFooter({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{
      background: theme.altBg,
      padding: isMobile ? '28px 16px 20px' : '48px 56px 28px',
      borderTop: `1px solid rgba(124,245,185,0.16)`,
      color: theme.fg,
      fontFamily: theme.font,
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.4fr 2.4fr 1fr',
          gap: isMobile ? 24 : 40,
          paddingBottom: isMobile ? 20 : 28,
        }}>
          {/* Brand */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 17, color: theme.fg, textDecoration: 'none' }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, background: theme.accent }}/>
              {brand}
              <span style={{
                fontSize: 11, fontWeight: 600, color: theme.accent,
                padding: '2px 8px', borderRadius: 999,
                background: 'rgba(124, 245, 185, 0.13)', marginLeft: 4,
              }}>
                AI-KOMBI
              </span>
            </Link>
            <p style={{
              fontSize: 13, lineHeight: 1.65, color: theme.creamMuted, margin: 0,
              maxWidth: 280,
            }}>
              {`Der Energie-Assistent von ${brand} verbindet KI-Beratung mit regionalen Fachbetrieben — für Solar, Wärmepumpe und Speicher.`}
            </p>
          </div>

          {/* Nav columns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? 18 : 28,
          }}>
            {footerCols.map((col) => (
              <div key={col.h} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{
                  fontSize: 10.5, color: theme.accent, fontFamily: theme.mono,
                  letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600,
                }}>
                  {col.h}
                </div>
                {col.items.map((item) => (
                  <Link key={item.label} href={item.href} style={{
                    fontSize: 13, lineHeight: 1.5, color: theme.fg,
                    cursor: 'pointer', textDecoration: 'none',
                  }}>
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}
          </div>

          {/* Status pill */}
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: isMobile ? 'flex-start' : 'flex-end',
            gap: 12,
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '8px 14px', borderRadius: 999,
              background: 'rgba(124,245,185,0.10)',
              border: `1px solid ${theme.glassBorder}`,
              fontSize: 12, fontWeight: 500,
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: 4, background: theme.accent,
                boxShadow: '0 0 0 3px rgba(124,245,185,0.20)',
              }}/>
              Energie-Assistent online
            </div>
            <div style={{
              fontSize: 11, color: theme.muted, fontFamily: theme.mono,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              textAlign: isMobile ? 'left' : 'right',
            }}>
              Antwort &lt; 60 Sek.
            </div>
          </div>
        </div>

        {/* Thin mint rule */}
        <div style={{
          height: 1, width: '100%',
          background: `linear-gradient(90deg, transparent 0%, ${theme.glassBorder} 50%, transparent 100%)`,
          marginBottom: 20,
        }}/>

        {/* Copyright */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 8 : 0,
          fontSize: isMobile ? 10 : 11, color: theme.muted, fontFamily: theme.mono,
          letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>
          <span>{`© ${new Date().getFullYear()} ${brand}`}</span>
          <span>Made in Berlin · v12.0</span>
        </div>
      </div>
    </footer>
  );
}
